import { useCallback, useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks';
import { getCurrentOrg } from 'store/groups/reducer';
import {
  getBillingStatus,
  fetchBillingBalance,
  fetchBillingBatches,
  fetchBillingInvoices,
  fetchBillingLedger,
  fetchBillingPaymentMethods,
  fetchBillingRateCard
} from 'store/billing/service';

export const useBillingGid = (): string => {
  const org = useAppSelector(getCurrentOrg);
  return org?.gid && org.gid !== 'all' ? org.gid : '';
};

export const useBillingLocale = (): string => useAppSelector((state) => state.intl.locale);

export const useBillingCurrency = (): string =>
  useAppSelector(
    (state) => state.billing.balance.data?.currencyCode || state.billing.rateCard.data?.currencyCode
  ) || 'USD';

export const useRefreshBilling = () => {
  const dispatch = useAppDispatch();
  const gid = useBillingGid();

  const refreshAll = useCallback(() => {
    if (!gid) return;
    dispatch(fetchBillingBalance(gid));
    dispatch(fetchBillingBatches({ gid, includeExpired: true }));
    dispatch(fetchBillingLedger({ gid }));
    dispatch(fetchBillingInvoices(gid));
    dispatch(fetchBillingPaymentMethods(gid));
    dispatch(fetchBillingRateCard(gid));
  }, [dispatch, gid]);

  const refreshCredits = useCallback(() => {
    if (!gid) return;
    dispatch(fetchBillingBalance(gid));
    dispatch(fetchBillingBatches({ gid, includeExpired: true }));
    dispatch(fetchBillingLedger({ gid }));
  }, [dispatch, gid]);

  const refreshPaymentMethods = useCallback(() => {
    if (!gid) return;
    dispatch(fetchBillingPaymentMethods(gid));
    dispatch(fetchBillingBalance(gid));
  }, [dispatch, gid]);

  return { refreshAll, refreshCredits, refreshPaymentMethods };
};

/**
 * What the organisation already told us: its legal name, the practice address it confirmed at
 * signup, and who owns the account. Fetched once per gid and handed to forms as a starting point,
 * never as a value they enforce (QA v26.10, #11).
 */
export const usePracticeProfile = (gid: string): IBillingStatus | null => {
  const [profile, setProfile] = useState<IBillingStatus | null>(null);

  useEffect(() => {
    if (!gid) {
      setProfile(null);
      return () => undefined;
    }
    let current = true;
    getBillingStatus(gid)
      .then((data) => current && setProfile(data))
      // A prefill that cannot be fetched is an empty form, not an error worth showing.
      .catch(() => current && setProfile(null));
    return () => {
      current = false;
    };
  }, [gid]);

  return profile;
};
